/**
 * Header behavior: sticky hide-on-scroll, nav dropdowns, the mobile menu drawer,
 * the search popout (a drawer on phones) and the country/language selectors.
 */

/** Keeps the header stuck to the top. With data-hide-on-scroll it also hides on scroll-down and returns on scroll-up. Publishes --header-height. */
class StickyHeader extends HTMLElement {
  connectedCallback() {
    // Expose the header's height (e.g. so the hero can fill the rest of the window).
    // The announcement bar scrolls away with the page while the rest of the header stays stuck, so its
    // height is published too (the sticky offset in sections/header.liquid is minus this).
    const announcement = this.querySelector('.announcement');
    this.resizeObserver = new ResizeObserver(() => {
      const root = document.documentElement.style;
      root.setProperty('--header-height', `${this.offsetHeight}px`);
      root.setProperty('--announcement-height', `${announcement ? announcement.offsetHeight : 0}px`);
    });
    this.resizeObserver.observe(this);
    if (announcement) this.resizeObserver.observe(announcement);

    if (!this.hasAttribute('data-sticky') || !this.hasAttribute('data-hide-on-scroll')) return;
    this.lastY = window.scrollY;
    this.ticking = false;
    this.onScroll = () => {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => this.update());
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
  }

  update() {
    const y = window.scrollY;
    const diff = y - this.lastY;
    if (Math.abs(diff) > 6) {
      // Keep the header in view while one of its menus or the search popout is open.
      const busy = this.querySelector('details[open], .header-search[open]');
      this.toggleAttribute('data-hidden', !busy && diff > 0 && y > 120);
      this.lastY = y;
    }
    this.ticking = false;
  }
}
customElements.define('sticky-header', StickyHeader);

/**
 * Nav item with a dropdown. The title is a link; the menu opens on hover (mouse) or via the chevron
 * toggle (click/keyboard/touch). Escape and outside clicks close it. In the full-width version,
 * hovering/focusing an item shows its previews.
 */
class NavDropdown extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');
    const canHover = window.matchMedia('(hover: hover)').matches;

    if (canHover) {
      this.addEventListener('mouseenter', () => this.setOpen(true));
      this.addEventListener('mouseleave', () => this.setOpen(false));
    }
    this.details.addEventListener('toggle', () => {
      this.summary.setAttribute('aria-expanded', String(this.details.open));
      if (this.details.open) {
        document.querySelectorAll('nav-dropdown').forEach((other) => other !== this && other.setOpen(false));
      }
    });
    this.onDocumentClick = (event) => {
      if (!this.contains(event.target)) this.setOpen(false);
    };
    this.onKeydown = (event) => {
      if (event.key === 'Escape' && this.details.open) {
        this.setOpen(false);
        this.summary.focus();
      }
    };
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);
    this.summary.setAttribute('aria-expanded', 'false');

    // Full-width menu: hovering or focusing an item on the left shows its product previews.
    this.megaItems = [...this.querySelectorAll('[data-mega-item]')];
    this.megaPanels = [...this.querySelectorAll('[data-mega-panel]')];
    this.megaItems.forEach((item, index) => {
      item.addEventListener('mouseenter', () => this.showPreview(index));
      item.addEventListener('focus', () => this.showPreview(index));
    });
  }

  showPreview(index) {
    this.megaItems.forEach((item, i) => item.classList.toggle('is-active', i === index));
    this.megaPanels.forEach((panel, i) => (panel.hidden = i !== index));
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
  }

  setOpen(open) {
    this.details.open = open;
  }
}
customElements.define('nav-dropdown', NavDropdown);

/** Slide-in drawer built on <dialog> (focus trap + Escape for free). Opened by any [data-drawer-open="<id>"]. */
class TeDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    });
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-drawer-close]')) this.close();
    });
  }

  open(trigger) {
    if (this.dialog.open) return;
    this.trigger = trigger;
    this.dialog.showModal();
    requestAnimationFrame(() => this.dialog.classList.add('is-open'));
    trigger?.setAttribute('aria-expanded', 'true');
    this.querySelector('[data-autofocus]')?.focus();
  }

  close() {
    if (!this.dialog.open) return;
    this.dialog.classList.remove('is-open');
    this.trigger?.setAttribute('aria-expanded', 'false');
    const duration = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 300;
    setTimeout(() => {
      this.dialog.close();
      this.trigger?.focus();
    }, duration);
  }
}
customElements.define('te-drawer', TeDrawer);

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-drawer-open]');
  if (!trigger) return;
  const drawer = document.getElementById(trigger.dataset.drawerOpen);
  if (drawer instanceof TeDrawer) drawer.open(trigger);
});

/**
 * Header search. Focusing the field dims the page behind a scrim; typing fetches live results
 * (sections/predictive-search.liquid) into a panel that grows out around the field. Below 768px
 * the same element is styled as a slide-in drawer. Closes on Escape, a scrim/close click, or
 * focus leaving the search.
 */
class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('.header-search__results');
    this.clearButton = this.querySelector('[data-search-clear]');
    this.toggles = document.querySelectorAll(`[aria-controls="${this.id}"]`);

    this.input.addEventListener('focus', () => this.open());
    this.input.addEventListener('input', () => {
      this.open();
      this.clearButton.hidden = !this.input.value;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.search(this.input.value.trim()), 250);
    });
    this.clearButton.addEventListener('click', () => {
      this.input.value = '';
      this.clearButton.hidden = true;
      this.search('');
      this.input.focus();
    });
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-search-close]')) {
        this.close();
        this.returnFocus();
      }
    });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.close();
        this.returnFocus();
      }
    });
    this.addEventListener('focusout', (event) => {
      if (event.relatedTarget && !this.contains(event.relatedTarget)) this.close();
    });
    this.toggles.forEach((toggle) =>
      toggle.addEventListener('click', () => {
        this.open();
        this.input.focus();
      })
    );
  }

  open() {
    if (this.hasAttribute('open') && !this.hasAttribute('data-closing')) return;
    clearTimeout(this.closeTimer);
    this.removeAttribute('data-closing');
    this.setAttribute('open', '');
    this.toggles.forEach((toggle) => toggle.setAttribute('aria-expanded', 'true'));
    // Let the results panel use the space left below the field.
    const top = this.querySelector('.header-search__form').getBoundingClientRect().bottom;
    this.style.setProperty('--search-results-max', `${Math.max(window.innerHeight - top - 32, 240)}px`);
    if (this.input.value.trim() && !this.results.childElementCount) this.search(this.input.value.trim());
  }

  close() {
    if (!this.hasAttribute('open') || this.hasAttribute('data-closing')) return;
    this.toggles.forEach((toggle) => toggle.setAttribute('aria-expanded', 'false'));
    if (this.contains(document.activeElement)) document.activeElement.blur();

    // Phones: let the drawer slide out before hiding it.
    const isDrawer = window.matchMedia('(max-width: 767px)').matches;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!isDrawer || reduceMotion) {
      this.removeAttribute('open');
      return;
    }
    this.setAttribute('data-closing', '');
    this.closeTimer = setTimeout(() => {
      this.removeAttribute('open');
      this.removeAttribute('data-closing');
    }, 250);
  }

  /** On phones the field is hidden once closed, so send focus back to the search icon. */
  returnFocus() {
    const visibleToggle = [...this.toggles].find((toggle) => toggle.offsetParent !== null);
    visibleToggle?.focus();
  }

  async search(query) {
    this.controller?.abort();
    if (!query) {
      this.results.replaceChildren();
      return;
    }

    if (!this.results.childElementCount) {
      const message = document.createElement('p');
      message.className = 'header-search__message';
      message.textContent = this.dataset.searching;
      this.results.replaceChildren(message);
    }
    this.setAttribute('aria-busy', 'true');

    this.controller = new AbortController();
    const params = new URLSearchParams({
      q: query,
      section_id: 'predictive-search',
      'resources[type]': 'product,query',
      'resources[limit]': '8',
      'resources[limit_scope]': 'each',
      'resources[options][fields]': 'title,product_type,vendor,variants.sku,tag',
    });

    try {
      const response = await fetch(`${this.dataset.url}?${params}`, { signal: this.controller.signal });
      if (!response.ok) throw new Error(response.statusText);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = html.getElementById('PredictiveSearchResults');
      this.results.replaceChildren(...(content ? content.childNodes : []));
      this.removeAttribute('aria-busy');
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.results.replaceChildren();
      this.removeAttribute('aria-busy');
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);

/** Country / language <select> that submits the localization form on change. */
class LocalizationSelect extends HTMLElement {
  connectedCallback() {
    this.querySelector('select').addEventListener('change', () => this.closest('form')?.submit());
  }
}
customElements.define('localization-select', LocalizationSelect);
